import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, merge, startWith, tap } from 'rxjs';
import { KundeTokenService } from '@comparit/infrastructure';
import { TaskContextService } from '@comparit/shared';
import { KfzRoutes } from '../app/app.routes.paths';
import { prefillStammdaten } from '../helpers/kunde-stammdaten-prefill';
import { skipReauth } from '../interceptors/kunde-reauth.interceptor';
import { KundeLoginResponse, KundeService, Sparte } from '@comparit/common-api';
import { FormsService } from './forms.service';
import { KundeLoginDialogService } from './kunde-login-dialog.service';

@Injectable()
export class KundeTaskStartService {
  private readonly router = inject(Router);
  private readonly kundeService = inject(KundeService);
  private readonly kundeToken = inject(KundeTokenService);
  private readonly taskContext = inject(TaskContextService);
  private readonly loginDialog = inject(KundeLoginDialogService);
  private readonly formsService = inject(FormsService);
  private readonly destroyRef = inject(DestroyRef);

  private armed = true;

  init(): void {
    const navigated$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    );
    const journeyReset$ = this.formsService.journeyReset$.pipe(
      tap(() => (this.armed = true)),
      map(() => this.router.url)
    );

    merge(navigated$, journeyReset$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(url => this.startIfTaskless(url));
  }

  private startIfTaskless(url: string): void {
    if (!this.armed || !url.startsWith(KfzRoutes.formular)) return;
    if (this.taskContext.taskId() !== null || !this.loginDialog.available()) return;

    const token = this.kundeToken.getToken();
    if (!token) return;

    this.armed = false;
    this.kundeService
      .startKundeTask(
        { kundeToken: token, startKundeTaskRequest: { sparte: Sparte.Kfz } },
        'body',
        false,
        { context: skipReauth() }
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.adopt(response),
        error: (error: unknown) => this.handleFailure(error, token),
      });
  }

  private adopt(response: KundeLoginResponse): void {
    if (this.taskContext.taskId() !== null || !this.kundeToken.isLoggedIn()) return;

    this.kundeToken.refreshToken(response.token, response.gueltigBis);
    this.taskContext.setTaskId(response.taskId);
    prefillStammdaten(this.formsService.zurPersonFormCreator, response.stammdaten);
  }

  private handleFailure(error: unknown, token: string): void {
    console.warn('KundeTaskStartService: no task could be started for the customer', error);
    const status = error instanceof HttpErrorResponse ? error.status : 0;

    if (status === 401) {
      if (this.kundeToken.getToken() === token) this.kundeToken.clear();
      return;
    }

    if (status === 0 || status >= 500) this.armed = true;
  }
}
